import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import dayjs from 'dayjs';
import Icons from '../../../../../components/Common/Icons/Icons';
import Checkbox from '../../../../../components/Common/UI/Checkbox';
import { PdfViewerTrigger } from '../../../../../context/pdfViewerDialog';
import { cn } from '../../../../../utils/styles';

export enum EnumBatchDocumentsStatus {
  'ready' = 'Ready',
  'pending' = 'Pending',
}

export interface IBatchDocumentsData {
  id: string;
  submittedDate: string;
  from: string;
  to: string;
  accounts: string;
  documentsType: string;
  status: EnumBatchDocumentsStatus;
  type: string;
  expiration: string;
}

export enum DocumentTypesEnum {
  'statements' = 'Statements',
  'taxes' = 'Taxes',
  'notices' = 'Notices',
}

export interface ISearchResultData {
  id: string;
  account: string;
  checked?: boolean;
  date: string;
  documentType: string;
  pageCount: string;
  from: string;
  to: string;
}

export const batchDocumentsCol: ColumnDef<IBatchDocumentsData>[] = [
  {
    header: 'Submitted Date',
    accessorKey: 'submittedDate',
    enableSorting: true,
  },
  {
    header: 'From',
    accessorKey: 'from',
    enableSorting: true,
    cell: ({ row }) => <div className="uppercase">{row.getValue('from')}</div>,
    meta: {
      mobileHidden: true,
    },
  },
  {
    header: 'To',
    accessorKey: 'to',
    enableSorting: true,
    cell: ({ row }) => <div className="uppercase">{row.getValue('to')}</div>,
    meta: {
      mobileHidden: true,
    },
  },
  {
    header: 'Accounts',
    accessorKey: 'accounts',
    enableSorting: true,
    cell: ({ row }) => (
      <div className="xxl:block flex gap-[10px] items-center">
        {row.getValue('accounts')}
        <a href="#" className="text-[#1119FF] hover:underline block">
          More
        </a>
      </div>
    ),
    meta: {
      mobileHidden: true,
    },
  },
  {
    header: 'Documents type',
    accessorKey: 'documentsType',
    enableSorting: true,
    cell: ({ row }) => (
      <div className="xxl:block flex gap-[10px] items-center">
        {row.getValue('documentsType')}
        <a href="#" className="text-[#1119FF] hover:underline block">
          More
        </a>
      </div>
    ),
    meta: {
      mobileHidden: true,
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: true,
    cell: ({ row }) => (
      <div
        className={cn('w-[95px] h-[26px] border rounded-[25px] flex items-center justify-center p-[5px]', {
          'border-[#11CE8C] text-[#11CE8C]': row.getValue('status') === EnumBatchDocumentsStatus.ready,
          'border-[#FF8057] text-[#FF8057]': row.getValue('status') === EnumBatchDocumentsStatus.pending,
        })}
      >
        {row.getValue('status') === EnumBatchDocumentsStatus.ready ? 'Ready' : 'Pending'}
      </div>
    ),
  },
  {
    header: 'Type',
    accessorKey: 'type',
    enableSorting: true,
  },
  {
    header: 'Expiration',
    accessorKey: 'expiration',
    enableSorting: true,
    meta: {
      mobileHidden: true,
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    accessorKey: '',
    cell: () => (
      <PdfViewerTrigger src="/infoTrac/jobDetails/statement.pdf">
        <a
          href="#"
          aria-label="download-pdf"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          className="border-[1px] xxl:h-[32px] h-[44px] flex xxl:w-[38px] w-[250px] border-primary bg-white text-primary
          px-[10px] py-[7px] hover:bg-primary hover:text-white rounded-lg items-center"
        >
          <Icons.DownloadIcon className="xxl:block hidden" />
          <div className="xxl:hidden">Download</div>
        </a>
      </PdfViewerTrigger>
    ),
    meta: {
      mobileHidden: true,
    },
  },
];

const now = dayjs();
const formatDate = (date: dayjs.Dayjs): string => `${date.format('MMM DD, YYYY HH:mm')} PDT`;

export const batchDocumentsData: IBatchDocumentsData[] = [
  {
    id: '1',
    submittedDate: formatDate(now),
    from: `MAR 31, ${now.year() - 3}`,
    to: `DEC 31, ${now.year() - 1}`,
    accounts: '****4560',
    documentsType: 'Tax',
    status: EnumBatchDocumentsStatus.ready,
    type: 'COMBINED',
    expiration: formatDate(now.add(30, 'day')),
  },
];

export const searchResultCol: ColumnDef<ISearchResultData>[] = [
  {
    accessorKey: 'checked',
    header: '',
    size: 100,
    cell: ({ row }) => (
      <Checkbox
        variant="guidance"
        checked={row.getIsSelected()}
        onCheckedChange={value => row.toggleSelected(!!value)}
      />
    ),
  },
  {
    header: 'account#',
    accessorKey: 'account',
  },
  {
    header: 'Statement date',
    accessorKey: 'date',
  },
  {
    header: 'Document type',
    accessorKey: 'documentType',
  },
  {
    header: 'Page count',
    accessorKey: 'pageCount',
  },
];
